import {REACT_HOTMATHS_HOME_URL, REGISTER_URL} from '../routes';

/**
 * Cambridge LANTITE Edge (lantite.cambridge.edu.au, the NUMERACY brand) homepage copy.
 *
 * Carried across from the Struts pages `src/main/webapp/home/numeracy/indexStudent.jsp` and `indexLecturer.jsp`, in
 * the same spirit as `hotmaths.js`: order and hierarchy change, words do not. The two pages were the same apart from
 * the lecturers' trial, so they are one page here, with the lecturers' trial in the trial card.
 *
 * Where the brand differs from HOTmaths:
 * - No free trial for students. Lecturers ask their local sales representative for a 30-day trial, so the trial card
 *   lists the lecturer page's representatives by region (`trial.contacts`) rather than showing the trial form.
 * - Pricing is a bundle or a single component. The prices are copy on the old page too; they match the card products
 *   (455 numeracy and 528 literacy at $29.95, 532 the bundle at $44.95).
 * - Join now goes to the React registration page at `/home/registerReact`, as HOTmaths' React homepage does; its
 *   `/home/register` stays the Struts page while "/" is the JSP homepage. Both ask for the customer's university.
 * - The overview video is on YouTube.
 * - The privacy policy and terms were modals; they are React pages on the same bundle now (see `routes`), reading the
 *   same fragments the modals include.
 *
 * Not carried across: the contact form (the Struts `submitContactUs` action; "Email us" and Help are the routes to
 * support now), the Student/Lecturer switch, and the System Requirements modal.
 *
 * The homepage at "/" is still the JSP one, so like HOTmaths this is the /home/homeReact preview and its own links
 * stay on it.
 */
const CONTACT_EMAIL = 'lantiteedge@cambridge.org';

export const lantiteContent = {
    productName: 'Cambridge LANTITE Edge',

    links: {
        home: REACT_HOTMATHS_HOME_URL,
        register: REGISTER_URL,
    },

    /**
     * The logo lantite.cambridge.edu.au shows, in place of HOTmaths'. It is white lettering for the brand's navy, which
     * the header, hero and footer all are here (`theme`), so it is never drawn black. It already carries the Cambridge
     * name, so it stands alone.
     */
    logo: {
        src: '/users/branding/numeracy/img/CLElogo.png',
        alt: 'Cambridge LANTITE Edge',
        includesCambridge: true,
    },

    /** The colours of lantite.cambridge.edu.au: navy, teal and orange (components/brandThemes.module.css). */
    theme: 'numeracy',

    /** The registration page (src/main/ui/register). Its products for lecturers are the "Teacher" ones. */
    registration: {
        teacherLabel: 'Lecturer',
    },

    intro: {
        heading: 'Cambridge LANTITE Edge',
        // The opening of the pages' "About Cambridge LANTITE Edge".
        standfirst: 'Developed specifically for students of Educational courses to give them the guidance they need '
            + 'to pass the Literacy and Numeracy Test for initial Teacher Education students.',
        primaryAction: {label: 'Join now', href: REGISTER_URL},
        secondaryAction: {label: 'Pricing', href: '#ch-home-pricing'},
        // The banner behind the live site's sign-in form and the brand's lesson headers.
        backgroundImage: '/users/lesson/img/headImages/CLE.jpg',
    },

    /** What the About copy says the product gives a student. */
    features: {
        heading: 'What you get',
        items: [
            {label: 'Numeracy component', icon: 'numeracy'},
            {label: 'Literacy component', icon: 'literacy'},
            {label: 'Interactive widgets', icon: 'widgets'},
            {label: 'Walkthroughs', icon: 'walkthrough'},
            {label: 'Comprehensive testing area', icon: 'testing'},
            {label: 'Test preparation', icon: 'preparation'},
        ],
    },

    /** The lecturer page's "Try Cambridge LANTITE Edge", with its sales representatives. */
    trial: {
        sectionHeading: 'Try Cambridge LANTITE Edge',
        heading: 'Lecturers, request your 30-day trial',
        body: 'If you are a Lecturer and would like to explore Cambridge LANTITE Edge contact your local sales '
            + 'representative to get started:',
        contacts: [
            {region: 'Queensland, Northern Territory & New Zealand', name: 'Steve Diack',
                email: 'steve.diack@cambridge.org', phone: '+61 497 881 119'},
            {region: 'Victoria, South Australia, Tasmania & ACT (Canberra)', name: 'Michael Shaw',
                email: 'michael.shaw@cambridge.org', phone: '+61 439 273 850'},
            {region: 'New South Wales & Western Australia', name: 'Nicole Mottlee',
                email: 'nicole.mottlee@cambridge.org', phone: '+61 437 389 810'},
        ],
    },

    /** The pages' "Pricing" and "Availability". */
    pricing: {
        heading: 'Pricing',
        body: 'Cambridge LANTITE Edge is available for both individual student or faculty-wide purchase. To ensure '
            + 'that students only pay for what they need, the literacy and numeracy components can be purchased '
            + 'separately, or bundled together at a discounted price. Lecturers, please email us to request your '
            + '30-day trial and receive further pricing information.',
        // "(inc GST)" starts the note: beside the price it took the figure onto a second line.
        options: [
            {label: 'Bundle', price: '$44.95 AUD',
                note: '(inc GST) Includes a 12-month subscription to both the literacy and numeracy components'},
            {label: 'Single', price: '$29.95 AUD',
                note: '(inc GST) Includes a 12-month subscription to either the literacy or numeracy component'},
        ],
        action: {label: 'Join now', href: REGISTER_URL},
        contactEmail: CONTACT_EMAIL,
    },

    video: {
        heading: 'See it in action',
        youtubeId: 'x4_5r12kzkk',
        title: 'Cambridge LANTITE Edge overview',
    },

    about: {
        heading: 'About Cambridge LANTITE Edge',
        paragraphs: [
            'Cambridge LANTITE Edge has been developed specifically for students of Educational courses to give them '
                + 'the guidance they need to pass the Literacy and Numeracy Test for initial Teacher Education '
                + 'students.',
            'This fully interactive digital test preparation product allows students to gain a thorough understanding '
                + 'of all the content required for the test, and to build confidence in their abilities by accessing '
                + 'the comprehensive testing area.',
            'The interactive widgets and walkthroughs give students a visual representation of key mathematical '
                + 'concepts, allowing students to gain a more thorough understanding of all content needed for the '
                + 'test, and solidifying their understanding for future teaching.',
        ],
    },

    /**
     * The brand's other public pages, rendered by the homepage bundle and matched with wouter (index.js). The server
     * serves these paths directly (WEB-INF/urlrewrite.xml).
     */
    routes: [
        {path: '/home/numeracy/terms', page: 'legal', title: 'Terms and Conditions',
            fragment: '/home/numeracy/fragments/terms.html'},
        {path: '/home/numeracy/privacy', page: 'legal', title: 'Privacy Policy',
            fragment: '/home/numeracy/fragments/privacy.html'},
    ],

    /** From the old footer (footerLinks.jsp), which opened these as modals. */
    footerLinks: [
        {label: 'Privacy Policy', href: '/home/numeracy/privacy'},
        {label: 'Terms and Conditions', href: '/home/numeracy/terms'},
    ],

    nav: [
        {label: 'Home', href: REACT_HOTMATHS_HOME_URL},
        // Sections of the homepage, written with its path so they also work from the legal pages.
        {label: 'Overview', href: `${REACT_HOTMATHS_HOME_URL}#ch-home-video`},
        {label: 'Pricing', href: `${REACT_HOTMATHS_HOME_URL}#ch-home-pricing`},
        {label: 'Help', href: '/usersCommon/showHelp.action'},
    ],
};

export default lantiteContent;
